"use client";

import { useCallback, useState } from "react";
import {
  Camera,
  Mic,
  Volume2,
  Wifi,
  Check,
  Circle,
  Loader2,
  PartyPopper,
} from "lucide-react";
import Link from "next/link";
import { buttonVariants } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { CameraCheck } from "./camera-check";
import { MicCheck } from "./mic-check";
import { SpeakerCheck } from "./speaker-check";
import { NetworkCheck } from "./network-check";

type Step = "camera" | "mic" | "speaker" | "network";
type StepStatus = "pending" | "active" | "passed";

const STEPS: {
  key: Step;
  label: string;
  icon: typeof Camera;
}[] = [
  { key: "camera", label: "Kamera", icon: Camera },
  { key: "mic", label: "Mikrofon", icon: Mic },
  { key: "speaker", label: "Zvučnik", icon: Volume2 },
  { key: "network", label: "Internet konekcija", icon: Wifi },
];

export function DeviceCheck() {
  const [statuses, setStatuses] = useState<Record<Step, StepStatus>>({
    camera: "active",
    mic: "pending",
    speaker: "pending",
    network: "pending",
  });

  const activeStep = (Object.entries(statuses) as [Step, StepStatus][]).find(
    ([, s]) => s === "active",
  )?.[0];

  const allPassed = Object.values(statuses).every((s) => s === "passed");

  const handlePass = useCallback((step: Step) => {
    setStatuses((prev) => {
      const next = { ...prev, [step]: "passed" as StepStatus };
      const stepOrder: Step[] = ["camera", "mic", "speaker", "network"];
      const currentIdx = stepOrder.indexOf(step);
      const nextStep = stepOrder[currentIdx + 1];
      if (nextStep && next[nextStep] === "pending") {
        next[nextStep] = "active";
      }
      return next;
    });
  }, []);

  return (
    <div className="space-y-4">
      {STEPS.map(({ key, label, icon: Icon }) => {
        const status = statuses[key];
        return (
          <div
            key={key}
            className={cn(
              "rounded-xl border transition-all",
              status === "active" && "border-primary bg-card shadow-xs",
              status === "passed" && "border-green-200 bg-green-50/50",
              status === "pending" && "border-border bg-card/50",
            )}
          >
            <div className="flex items-center gap-3 p-4">
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                  status === "active" && "bg-primary/10 text-primary",
                  status === "passed" && "bg-green-100 text-brand",
                  status === "pending" && "bg-muted text-muted-foreground",
                )}
              >
                {status === "passed" ? (
                  <Check className="h-4 w-4" />
                ) : status === "active" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Circle className="h-4 w-4" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <Icon
                  className={cn(
                    "h-4 w-4",
                    status === "passed"
                      ? "text-brand"
                      : status === "active"
                        ? "text-foreground"
                        : "text-muted-foreground",
                  )}
                />
                <span
                  className={cn(
                    "text-sm font-medium",
                    status === "pending" && "text-muted-foreground",
                  )}
                >
                  {label}
                </span>
              </div>
              {status === "passed" && (
                <span className="text-brand ml-auto text-xs font-medium">
                  OK
                </span>
              )}
            </div>

            {status === "active" && (
              <div className="border-t px-4 pb-4 pt-3">
                {key === "camera" && (
                  <CameraCheck onPass={() => handlePass("camera")} />
                )}
                {key === "mic" && <MicCheck onPass={() => handlePass("mic")} />}
                {key === "speaker" && (
                  <SpeakerCheck onPass={() => handlePass("speaker")} />
                )}
                {key === "network" && (
                  <NetworkCheck onPass={() => handlePass("network")} />
                )}
              </div>
            )}
          </div>
        );
      })}

      {allPassed && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-6 text-center">
          <PartyPopper className="text-brand mx-auto mb-3 h-8 w-8" />
          <h2 className="text-lg font-semibold text-green-700">
            Sve je spremno!
          </h2>
          <p className="text-brand mt-1 text-sm">
            Kamera, mikrofon, zvučnik i internet konekcija rade ispravno.
          </p>
          <Link
            href="/app/inbox"
            className={buttonVariants({
              className: "mt-4",
            })}
          >
            Nazad na inbox
          </Link>
        </div>
      )}
    </div>
  );
}
